import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Badge,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Grow,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import AddIcon from '@material-ui/icons/Add';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import EditIcon from '@material-ui/icons/Edit';
import PublishIcon from '@material-ui/icons/Publish';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import jstring from '@easytech-international-sdn-bhd/jstring';
import {useSnackbar} from 'notistack';
import API_Service, {axiosURIencode} from '../../../../Services/API_Service';
import {req_gallery_type, req_gallery_type_seq, req_gallery_type_status} from '../../../../Helper/Constants';
import {EmptyState, SectionToolbar, SettingsSurface, StatusSwitch, StatusTabs} from './ui';

const useStyles = makeStyles((theme) => ({
    addButton: {
        width: 40,
        height: 40,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        backgroundColor: theme.palette.primary.main,
        boxShadow: '0 7px 16px rgba(0,0,0,.16)',
        transition: 'transform 140ms cubic-bezier(.34,1.3,.64,1)',
        '&:hover': {backgroundColor: theme.palette.primary.main, transform: 'translateY(-2px) scale(1.04)'}
    },
    publish: {borderRadius: 10, fontWeight: 750},
    table: {minWidth: 540},
    row: {
        transition: 'background-color 120ms ease, box-shadow 140ms ease, transform 140ms ease',
        '&:hover': {backgroundColor: theme.palette.action.hover},
        '& .MuiTableCell-root': {borderBottomColor: theme.palette.divider}
    },
    dragging: {
        display: 'table',
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 18px 38px rgba(0,0,0,.2)',
        '& .MuiTableCell-root:first-child': {borderLeft: `3px solid ${theme.palette.primary.main}`}
    },
    dragHandle: {
        display: 'inline-flex',
        color: theme.palette.text.secondary,
        cursor: 'grab',
        '&:active': {cursor: 'grabbing'},
        '&:focus-visible': {outline: `2px solid ${theme.palette.primary.main}`}
    },
    name: {fontWeight: 700},
    commission: {
        display: 'inline-flex',
        padding: theme.spacing(0.5, 1),
        borderRadius: 999,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.action.hover,
        fontSize: 12,
        fontWeight: 700
    },
    loader: {padding: theme.spacing(1, 2)},
    dialogPaper: {borderRadius: 16},
    dialogTitle: {fontWeight: 800},
    field: {'& .MuiOutlinedInput-root': {borderRadius: 11}}
}));

const isEditable = (item) => /^\d+$/.test(String(item.type_id));

const searchItems = (items, query) => {
    const normalized = jstring(query);
    if (!normalized.isValid()) return items;
    const tokens = normalized.toString().split(' ');
    return items.filter((item) => tokens.every((token) => jstring(item.type_name).searchDeep(token)));
};

export default function PhotoTypeV1Section({axiosConfig, axiosPut, language}) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const {enqueueSnackbar} = useSnackbar();
    const [items, setItems] = useState(null);
    const [statusFilter, setStatusFilter] = useState(1);
    const [search, setSearch] = useState('');
    const [moveCount, setMoveCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [name, setName] = useState('');
    const [commission, setCommission] = useState(0);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setItems(null);
        API_Service.get(req_gallery_type, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) setItems(data.extraData || []);
            else {
                setItems([]);
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setItems([]);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }, [axiosConfig, enqueueSnackbar, language.operation_fail]);

    useEffect(() => { load(); }, [load]);

    const statusItems = useMemo(() => (items || []).filter((item) => parseInt(item.type_status) === parseInt(statusFilter)), [items, statusFilter]);
    const displayed = useMemo(() => searchItems(statusItems, search), [search, statusItems]);

    const updateStatusFilter = (value) => {
        setStatusFilter(value);
        setPage(0);
        setMoveCount(0);
    };

    const onDragEnd = ({source, destination}) => {
        if (!destination || destination.index === source.index || search) return;
        const sourceIndex = page * rowsPerPage + source.index;
        const destinationIndex = page * rowsPerPage + destination.index;
        const reordered = [...statusItems];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(destinationIndex, 0, moved);
        const other = (items || []).filter((item) => parseInt(item.type_status) !== parseInt(statusFilter));
        setItems([...other, ...reordered]);
        setMoveCount((count) => count + 1);
    };

    const publish = () => {
        if (!moveCount) return;
        const formdata = new FormData();
        formdata.append('array', JSON.stringify(statusItems.map((item, index) => ({type_id: item.type_id, sequence_no: index + 1}))));
        API_Service.post(req_gallery_type_seq, formdata, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                setMoveCount(0);
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'}));
    };

    const [togglingId, setTogglingId] = useState(null);

    const toggleStatus = (item) => {
        setTogglingId(item.type_id);
        API_Service.put(req_gallery_type_status, axiosURIencode({type_id: item.type_id}), axiosPut).then(({data}) => {
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
        }).catch(() => enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'}))
            .finally(() => setTogglingId(null));
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setEditItem(null);
        setName('');
        setCommission(0);
    };

    const openAdd = () => {
        setEditItem(null);
        setName('');
        setCommission(0);
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditItem(item);
        setName(item.type_name || '');
        setCommission(item.type_commission == null ? 0 : item.type_commission);
        setDialogOpen(true);
    };

    const save = () => {
        if (!name.trim()) {
            enqueueSnackbar(language.name_required || 'Name cannot be blank', {variant: 'error'});
            return;
        }
        setSaving(true);
        const request = editItem
            ? API_Service.put(req_gallery_type, axiosURIencode({
                type_id: editItem.type_id,
                type_name: name,
                type_commission: commission
            }), axiosPut)
            : (() => {
                const formdata = new FormData();
                formdata.append('type_name', name);
                formdata.append('type_commission', commission);
                formdata.append('version', 1);
                return API_Service.post(req_gallery_type, formdata, axiosConfig);
            })();
        request.then(({data}) => {
            setSaving(false);
            if (parseInt(data.result) === 1) {
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                setDialogOpen(false);
                setEditItem(null);
                setName('');
                setCommission(0);
                load();
            } else enqueueSnackbar(data.message || language.operation_fail, {variant: 'warning'});
        }).catch(() => {
            setSaving(false);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    };

    const pageItems = displayed.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <React.Fragment>
            <SectionToolbar search={search} onSearch={(value) => { setSearch(value); setPage(0); }} placeholder={language.search}>
                <StatusTabs value={statusFilter} onChange={updateStatusFilter} activeLabel={language.active} inactiveLabel={language.inactive}/>
                <Badge badgeContent={moveCount} color="error" overlap="rectangular">
                    <Button className={classes.publish} variant="outlined" disabled={moveCount === 0} startIcon={<PublishIcon/>} onClick={publish}>
                        {language.publish_sequence || language.update}
                    </Button>
                </Badge>
                <Tooltip title={language.add}>
                    <IconButton className={classes.addButton} onClick={openAdd} aria-label={language.add}><AddIcon/></IconButton>
                </Tooltip>
            </SectionToolbar>
            <SettingsSurface>
                {items === null ? (
                    <div className={classes.loader}>{[0, 1, 2, 3].map((row) => <Skeleton key={row} animation="wave" height={58}/>)}</div>
                ) : displayed.length === 0 ? <EmptyState label={language.no_data}/> : (
                    <React.Fragment>
                        <TableContainer>
                            <Table className={classes.table} size="small" aria-label={language.photo_type}>
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="photo-type-v1" direction="vertical">
                                        {(provided) => (
                                            <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                                {pageItems.map((item, index) => (
                                                    <Draggable key={String(item.type_id)} draggableId={String(item.type_id)} index={index} isDragDisabled={Boolean(search)}>
                                                        {(dragProvided, snapshot) => (
                                                            <TableRow
                                                                ref={dragProvided.innerRef}
                                                                {...dragProvided.draggableProps}
                                                                className={`${classes.row} ${snapshot.isDragging ? classes.dragging : ''}`}
                                                                style={dragProvided.draggableProps.style}
                                                            >
                                                                <TableCell width={48}>
                                                                    <span className={classes.dragHandle} {...dragProvided.dragHandleProps} tabIndex={search ? -1 : 0} aria-label={language.reorder || 'Reorder'}>
                                                                        <DragIndicatorIcon fontSize="small"/>
                                                                    </span>
                                                                </TableCell>
                                                                <TableCell className={classes.name}>{item.type_name}</TableCell>
                                                                <TableCell align="right"><span className={classes.commission}>{language.commission || 'Commission'} · {item.type_commission}</span></TableCell>
                                                                <TableCell align="right" width={96}>
                                                                    {isEditable(item) ? (
                                                                        <Tooltip title={language.edit}>
                                                                            <IconButton size="small" onClick={() => openEdit(item)}><EditIcon fontSize="small"/></IconButton>
                                                                        </Tooltip>
                                                                    ) : null}
                                                                    <StatusSwitch active={parseInt(item.type_status) === 1} pending={String(togglingId) === String(item.type_id)} name={item.type_name} language={language} onToggle={() => toggleStatus(item)}/>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </TableBody>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={displayed.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[100, 140, 165, 200]}
                            onPageChange={(event, value) => setPage(value)}
                            onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
                        />
                    </React.Fragment>
                )}
            </SettingsSurface>
            <Dialog open={dialogOpen} onClose={closeDialog} fullScreen={fullScreen} fullWidth maxWidth="sm" TransitionComponent={Grow} PaperProps={{className: classes.dialogPaper}}>
                <DialogTitle className={classes.dialogTitle}>{editItem ? `${language.edit} · ${language.photo_type}` : (language.new_photo_category || `${language.add} ${language.photo_type}`)}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={7}>
                            <TextField className={classes.field} autoFocus fullWidth required variant="outlined" label={language.name || language.photo_type} value={name} onChange={(event) => setName(event.target.value)} /* inputProps={{maxLength: 25}} */ />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField className={classes.field} fullWidth variant="outlined" type="number" label={language.commission || 'Commission'} value={commission} onChange={(event) => setCommission(event.target.value)} inputProps={{min: 0}}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button disabled={saving} onClick={closeDialog}>{language.cancel}</Button>
                    <Button variant="contained" color="primary" disabled={saving || !name.trim()} onClick={save}>
                        {saving ? <CircularProgress size={18}/> : editItem ? language.update : language.add}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
